import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil } from 'lucide-react';
import { AppContext } from '@/App';
import { useProjectDirectory } from '@/hooks/useProjectDirectory';
import { ProjectMasterForm } from './ProjectMasterForm';

export const ProjectMasterData = ({ refreshKey }) => {
  const { API } = useContext(AppContext);
  const [revision, setRevision] = useState(0);
  const directory = useProjectDirectory({ includeCompleted: true, refreshKey: `${refreshKey}:${revision}` });
  const [descriptions, setDescriptions] = useState({});
  const [legacy, setLegacy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const onFocus = () => setRevision((v) => v + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  useEffect(() => {
    if (directory.loading) return;
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([
      axios.get(`${API}/projects/ongoing`),
      axios.get(`${API}/projects/completed`),
      axios.get(`${API}/master-data/projects/project_types`),
    ]).then(async ([ongoing, completed, oldTypes]) => {
      const details = new Map([...ongoing.data, ...completed.data].map((p) => [p.id, p.description || '']));
      // Existing lifecycle lists exclude inactive rows and have historical caps.
      // Resolve only missing descriptions using the existing detail endpoint.
      const missing = directory.projects.filter((p) => !details.has(p.id));
      const extra = await Promise.all(missing.map((p) => axios.get(`${API}/projects/${p.id}`)));
      extra.forEach(({ data }) => details.set(data.id, data.description || ''));
      if (!active) return;
      setDescriptions(Object.fromEntries(details));
      setLegacy(oldTypes.data.data || []);
    }).catch(() => { if (active) setError('Could not load project details or old project types.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [API, directory.projects, directory.loading, refreshKey, revision]);

  const term = search.trim().toLowerCase();
  const rows = directory.projects.filter((p) => `${p.id} ${p.name} ${descriptions[p.id] || ''} ${p.status}`.toLowerCase().includes(term));
  const busy = loading || directory.loading;
  return (
    <section className="min-w-0 space-y-5" data-testid="master-real-projects">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold" data-testid="master-real-projects-title">Projects</h2>
        <button onClick={() => { setEditing({}); setNotice(''); setRevision((v) => v + 1); }} disabled={busy || Boolean(error || directory.error)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 disabled:opacity-50 transition-colors" data-testid="master-project-add"><Plus size={16} />Add project</button>
      </div>
      <input type="search" aria-label="Search projects" placeholder="Search PRJ code, name, description or status…" value={search} onChange={(e) => setSearch(e.target.value)}
        className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" data-testid="master-project-search" />
      {notice && <p role="status" className="text-green-700" data-testid="master-project-success">{notice}</p>}
      {(error || directory.error) && <div role="alert" className="text-red-700 text-sm" data-testid="master-project-load-error">
        {error || directory.error} <button onClick={() => setRevision((v) => v + 1)} className="underline" data-testid="master-project-retry">Retry</button>
      </div>}
      {editing && <ProjectMasterForm key={editing.id || 'new'} record={editing} projects={directory.projects}
        onCancel={() => setEditing(null)} onSaved={() => { setNotice('Project saved.'); setEditing(null); setRevision((v) => v + 1); }} />}
      {busy ? <p data-testid="master-project-loading">Loading projects…</p> : <>
        <div className="hidden md:grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,3fr)_7rem_3rem] gap-4 py-2 border-b text-xs font-semibold text-gray-500 uppercase" data-testid="master-project-columns">
          <span>PRJ code</span><span>Name</span><span>Description</span><span>Status</span><span>Edit</span>
        </div>
        <div data-testid="master-project-list">
          {rows.map((p) => <div key={p.id} data-testid={`master-project-row-${p.id}`} className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,3fr)_7rem_3rem] gap-2 md:gap-4 py-4 border-b border-gray-200 text-sm break-words">
            <span className="font-mono text-indigo-800" data-testid={`master-project-code-${p.id}`}>{p.id}</span>
            <span className="font-semibold" data-testid={`master-project-name-${p.id}`}>{p.name}</span>
            <span className="text-gray-600" data-testid={`master-project-description-${p.id}`}>{descriptions[p.id] || '—'}</span>
            <span data-testid={`master-project-status-${p.id}`} className={p.status === 'Ongoing' ? 'text-green-700' : 'text-gray-600'}>{p.status}</span>
            <button type="button" title={`Edit ${p.id}`} aria-label={`Edit ${p.id}`} data-testid={`master-project-edit-${p.id}`}
              onClick={async () => {
                try {
                  const { data } = await axios.get(`${API}/projects/${p.id}`);
                  setEditing({ id: p.id, name: data.name, description: data.description || '' }); setNotice('');
                } catch { setError('Could not load the project for editing.'); }
              }} className="w-8 h-8 flex items-center justify-center text-indigo-700 hover:bg-indigo-50 rounded transition-colors"><Pencil size={16} /></button>
          </div>)}
          {!rows.length && <p className="py-6 text-gray-500" data-testid="master-project-empty">{term ? 'No matching projects.' : 'No projects yet.'}</p>}
        </div>
      </>}
      <section className="pt-6 border-t border-gray-200 space-y-3" data-testid="old-project-types">
        <h2 className="text-lg font-semibold" data-testid="old-project-types-title">Old project types (not used in POs)</h2>
        <p className="text-sm text-gray-500" data-testid="old-project-types-readonly">Read-only</p>
        {legacy.map((p, i) => <div key={p.id || i} className="grid grid-cols-1 md:grid-cols-2 gap-2 py-3 border-b text-sm break-words" data-testid={`old-project-type-${p.id || i}`}>
          <span data-testid={`old-project-type-name-${p.id || i}`}>{p.name || '—'}</span><span className="text-gray-600" data-testid={`old-project-type-description-${p.id || i}`}>{p.description || '—'}</span>
        </div>)}
        {!loading && !error && !legacy.length && <p className="text-sm text-gray-500" data-testid="old-project-types-empty">No old project types.</p>}
      </section>
    </section>
  );
};